/**
 * The password rules, mirrored for the browser.
 *
 * This exists so the checklist beside the field can fill in as somebody types,
 * rather than making them submit to find out. It is a copy of
 * apps/api/src/lib/password.js and the server remains the authority — nothing
 * here decides whether a password is accepted, it only predicts.
 *
 * A copy is a thing that drifts, so apps/api/tests/password-mirror.test.js runs
 * both implementations over the same corpus and fails if they ever disagree.
 * That is cheaper than the alternatives: a shared package for eighty lines, or
 * a round trip on every keystroke to tell somebody their password is too short.
 */

export const MIN_LENGTH = 12;
export const MAX_LENGTH = 72;

const COMMON = new Set([
  'password', 'password1', 'password123', 'password@123', 'passw0rd',
  'welcome', 'welcome1', 'welcome@123', 'welcome123',
  'admin', 'admin@123', 'admin123', 'administrator',
  'qwerty', 'qwerty123', 'qwertyuiop', 'asdfghjkl',
  'iloveyou', 'letmein', 'monkey', 'dragon', 'sunshine', 'princess', 'football',
  'abc123', '123456', '1234567', '12345678', '123456789', '1234567890',
  'india@123', 'india123', 'sevenmentor', 'sevenmentor@123', 'sevenmentor123',
  'changeme', 'changeme123', 'secret', 'temp@123', 'test@123', 'pune@123',
]);

export const RULES = [
  { id: 'length', label: `At least ${MIN_LENGTH} characters` },
  { id: 'lower', label: 'A lowercase letter' },
  { id: 'upper', label: 'An uppercase letter' },
  { id: 'digit', label: 'A number' },
  { id: 'symbol', label: 'A symbol, such as ! @ # or −' },
  { id: 'notCommon', label: 'Not a commonly used password' },
  { id: 'notPersonal', label: 'Nothing from your name, email or employee code' },
  { id: 'notRepeated', label: 'No long runs or straight sequences' },
];

const norm = (s) => String(s ?? '').toLowerCase();

function hasWeakRun(value) {
  const v = norm(value);
  for (let i = 0; i + 3 < v.length; i += 1) {
    const [a, b, c, d] = [v.charCodeAt(i), v.charCodeAt(i + 1), v.charCodeAt(i + 2), v.charCodeAt(i + 3)];
    if (a === b && b === c && c === d) return true;
    if (b - a === 1 && c - b === 1 && d - c === 1) return true;
    if (a - b === 1 && b - c === 1 && c - d === 1) return true;
  }
  return false;
}

function personalFragments(employee = {}) {
  const parts = [
    employee.firstName, employee.lastName, employee.fullName,
    employee.employeeCode,
    String(employee.email ?? '').split('@')[0],
  ];
  return parts
    .flatMap((p) => norm(p).split(/[^a-z0-9]+/))
    .filter((p) => p.length >= 4);
}

export function checkPassword(value, employee = {}) {
  const v = String(value ?? '');
  const lower = norm(v);
  const fragments = personalFragments(employee);

  const results = {
    length: v.length >= MIN_LENGTH && v.length <= MAX_LENGTH,
    lower: /[a-z]/.test(v),
    upper: /[A-Z]/.test(v),
    digit: /[0-9]/.test(v),
    symbol: /[^A-Za-z0-9]/.test(v),
    notCommon: !COMMON.has(lower) && ![...COMMON].some((c) => c.length >= 8 && lower.includes(c)),
    notPersonal: !fragments.some((f) => lower.includes(f)),
    notRepeated: !hasWeakRun(v),
  };

  const failed = RULES.filter((r) => !results[r.id]);
  return { ok: failed.length === 0, results, failed: failed.map((r) => r.id), rules: RULES };
}

/**
 * A coarse strength read, for the bar under the field.
 *
 * Counts satisfied rules and gives length its own weight, because a
 * twenty-character passphrase that happens to lack a symbol is stronger than a
 * twelve-character one that ticks every box. Deliberately not an entropy
 * estimate: a precise-looking score invites arguing with it.
 */
export function strengthOf(value, employee = {}) {
  const v = String(value ?? '');
  if (!v) return { score: 0, label: '', tone: '' };

  const { results } = checkPassword(v, employee);
  const met = Object.values(results).filter(Boolean).length;
  const lengthBonus = v.length >= 20 ? 2 : v.length >= 16 ? 1 : 0;
  const score = Math.min(4, Math.floor((met + lengthBonus) / 2.5));

  return [
    { score: 0, label: 'Too weak', tone: 'crit' },
    { score: 1, label: 'Weak', tone: 'crit' },
    { score: 2, label: 'Fair', tone: 'warn' },
    { score: 3, label: 'Strong', tone: 'good' },
    { score: 4, label: 'Very strong', tone: 'good' },
  ][score];
}
